import type { SourceScope } from "../../api/threads/batchesApi";

/**
 * The scope picker's choices, turned into one `source_scope@1` value (V-1): which
 * documents of the corpus version, then optionally only their segments of one type.
 */
export type DocumentChoice =
  | { readonly kind: "corpus" }
  | { readonly kind: "documents"; readonly keysText: string }
  | { readonly kind: "metadata"; readonly field: string; readonly value: string };

export interface ScopeForm {
  readonly documents: DocumentChoice;
  /** Empty: one item per document; otherwise one item per segment of this type. */
  readonly segmentType: string;
}

const SEGMENT_TYPE_PATTERN = /^[a-z][a-z0-9_]{0,62}$/;

/** The keys of a text with one key per line or separated by commas, blanks dropped. */
export function documentKeys(text: string): string[] {
  return [...new Set(text.split(/[\n,]/).map((key) => key.trim()).filter((key) => key !== ""))];
}

/** The scope, or the reason the form does not name one yet. */
export function scopeOf(form: ScopeForm): { readonly scope: SourceScope } | { readonly problem: "noDocuments" | "noMetadata" | "segmentType" } {
  let base: SourceScope;
  switch (form.documents.kind) {
    case "corpus":
      base = { corpus: "bound" };
      break;
    case "documents": {
      const keys = documentKeys(form.documents.keysText);
      if (keys.length === 0) {
        return { problem: "noDocuments" };
      }
      base = { documents: keys };
      break;
    }
    case "metadata": {
      const field = form.documents.field.trim();
      const value = form.documents.value.trim();
      if (field === "" || value === "") {
        return { problem: "noMetadata" };
      }
      base = { metadata: { [field]: value } };
      break;
    }
  }
  const segmentType = form.segmentType.trim();
  if (segmentType === "") {
    return { scope: base };
  }
  if (!SEGMENT_TYPE_PATTERN.test(segmentType)) {
    return { problem: "segmentType" };
  }
  return { scope: { segments: { type: segmentType, within: base } } };
}
